import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import api from "../../../api/axios";
import toast from "react-hot-toast";
import { Plus, Trash2, Edit, GripVertical, ExternalLink } from "lucide-react";

function StrictModeDroppable(props) {
  const [enabled, setEnabled] = useState(false);
  useEffect(() => {
    const animation = requestAnimationFrame(() => setEnabled(true));
    return () => {
      cancelAnimationFrame(animation);
      setEnabled(false);
    };
  }, []);
  if (!enabled) return null;
  return <Droppable {...props} />;
}

const toAbsoluteImageUrl = (path) => {
  if (!path) return "";
  if (/^https?:\/\//i.test(path) || path.startsWith("data:")) return path;

  const base = (process.env.REACT_APP_UPLOAD_URL || process.env.REACT_APP_BASE_URL || "").replace(/\/$/, "");
  const cleanPath = String(path).replace(/^\/+/, "");
  return base ? `${base}/${cleanPath}` : path;
};

export default function ContractedBankList() {
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchBanks = async () => {
    try {
      const res = await api.get("/contractedbank");
      const list = Array.isArray(res.data) ? res.data : Array.isArray(res.data?.$values) ? res.data.$values : [];
      const sorted = list
        .map((bank, i) => ({
          ...bank,
          imageUrl: toAbsoluteImageUrl(bank.bankLogo),
          sortOrder: bank.sortOrder || i + 1,
        }))
        .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
      setBanks(sorted);
    } catch {
      toast.error("Bankalar yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBanks();
  }, []);

  const handleDelete = (id) => {
    if (!window.confirm("Bu bankayı silmek istediğinize emin misiniz?"))
      return;
    api
      .delete(`/contractedbank/${id}`)
      .then(() => {
        toast.success("Banka silindi");
        fetchBanks();
      })
      .catch(() => toast.error("Silme başarısız"));
  };

  const onDragEnd = async (result) => {
    if (!result.destination || result.destination.index === result.source.index)
      return;

    const items = Array.from(banks);
    const [moved] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, moved);
    setBanks(items);

    try {
      await Promise.all(
        items.map((b, i) => {
          const fd = new FormData();
          fd.append("BankNameTr", b.bankNameTr || "");
          fd.append("BankNameEn", b.bankNameEn || "");
          fd.append("BankNameAr", b.bankNameAr || "");
          fd.append("BankUrl", b.bankUrl || "");
          fd.append("IsActive", b.isActive);
          fd.append("SortOrder", i + 1);
          return api.put(`/contractedbank/${b.contractedBankId}`, fd);
        })
      );
      toast.success("Sıralama güncellendi");
    } catch {
      toast.error("Sıralama güncellenemedi");
      fetchBanks();
    }
  };

  if (loading)
    return (
      <div className="page-loading">
        <div className="loading-spinner" />
        <p>Yükleniyor...</p>
      </div>
    );

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h2>Anlaşmalı Bankalar</h2>
          <p className="page-subtitle">{banks.length} banka listeleniyor</p>
        </div>
        <Link to="/add-contracted-bank" className="btn btn-primary">
          <Plus size={18} /> Yeni Banka
        </Link>
      </div>

      <div className="content-card no-padding">
        <DragDropContext onDragEnd={onDragEnd}>
          <div className="table-responsive">
            <table className="data-table">
              <thead>
                <tr>
                  <th style={{ width: 50 }}></th>
                  <th>Logo</th>
                  <th>Banka Adı</th>
                  <th>Website</th>
                  <th>Durum</th>
                  <th>İşlemler</th>
                </tr>
              </thead>
              <StrictModeDroppable droppableId="banks">
                {(provided) => (
                  <tbody ref={provided.innerRef} {...provided.droppableProps}>
                    {banks.map((bank, index) => (
                      <Draggable
                        key={String(bank.contractedBankId)}
                        draggableId={String(bank.contractedBankId)}
                        index={index}
                      >
                        {(provided, snapshot) => (
                          <tr
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            className={snapshot.isDragging ? "dragging" : ""}
                          >
                            <td
                              {...provided.dragHandleProps}
                              className="drag-handle"
                            >
                              <GripVertical size={18} />
                            </td>
                            <td>
                              {bank.imageUrl ? (
                                <img src={bank.imageUrl} alt={bank.bankNameTr} className="table-thumb" />
                              ) : (
                                <div className="table-thumb-placeholder">
                                  {(bank.bankNameTr || bank.bankNameEn)?.charAt(0)}
                                </div>
                              )}
                            </td>
                            <td className="fw-medium">
                              {bank.bankNameTr || bank.bankNameEn || bank.bankNameAr}
                            </td>
                            <td>
                              {bank.bankUrl ? (
                                <a
                                  href={bank.bankUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="link-with-icon"
                                >
                                  <ExternalLink size={14} /> Website
                                </a>
                              ) : (
                                <span className="text-muted">-</span>
                              )}
                            </td>
                            <td>
                              <span
                                className={`status-badge ${bank.isActive ? "active" : "inactive"}`}
                              >
                                {bank.isActive ? "Aktif" : "Pasif"}
                              </span>
                            </td>
                            <td>
                              <div className="action-buttons">
                                <Link
                                  to={`/edit-contracted-bank/${bank.contractedBankId}`}
                                  className="btn-icon edit"
                                  title="Düzenle"
                                >
                                  <Edit size={16} />
                                </Link>
                                <button
                                  onClick={() => handleDelete(bank.contractedBankId)}
                                  className="btn-icon delete"
                                  title="Sil"
                                >
                                  <Trash2 size={16} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </tbody>
                )}
              </StrictModeDroppable>
            </table>
          </div>
        </DragDropContext>

        {banks.length === 0 && (
          <div className="empty-state">
            <p>Henüz banka eklenmemiş</p>
            <Link to="/add-contracted-bank" className="btn btn-primary">
              <Plus size={18} /> İlk Bankayı Ekle
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
